import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icon paths (Webpack quirk)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const restaurantIcon = L.divIcon({
  className: "",
  html: `<div style="background:#E32636;color:#fff;width:36px;height:36px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:grid;place-items:center;border:3px solid #fff;box-shadow:0 4px 14px rgba(0,0,0,0.3);"><span style="transform:rotate(45deg);font-size:18px;">🍽️</span></div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

// Conakry approx bounding — keeps drift within the city
const CONAKRY_CENTER = [9.6412, -13.5784];

function Recenter({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, 16, { animate: true });
  }, [position, map]);
  return null;
}

function ClickHandler({ enabled, onSelect }) {
  useMapEvents({
    click(e) {
      if (!enabled) return;
      onSelect?.(+e.latlng.lat.toFixed(6), +e.latlng.lng.toFixed(6));
    },
  });
  return null;
}

/**
 * Restaurant location map.
 *
 * Props:
 *  - lat, lng: current position (numbers or null)
 *  - editable: bool — enable click-to-place + marker drag
 *  - onChange: (lat, lng) => void — called when user drops the pin
 *  - height: pixel height (default 260)
 */
export default function RestaurantLocationMap({ lat, lng, editable = false, onChange, height = 260 }) {
  const hasPos = Number.isFinite(lat) && Number.isFinite(lng);
  const position = useMemo(() => (hasPos ? [lat, lng] : null), [lat, lng, hasPos]);
  const center = position || CONAKRY_CENTER;

  return (
    <div
      style={{ height, width: "100%", borderRadius: "1rem", overflow: "hidden" }}
      className="border border-sand"
      data-testid="restaurant-location-map"
    >
      <MapContainer
        center={center}
        zoom={hasPos ? 16 : 12}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Recenter position={position} />
        <ClickHandler enabled={editable} onSelect={onChange} />
        {position && (
          <Marker
            position={position}
            icon={restaurantIcon}
            draggable={editable}
            eventHandlers={{
              dragend: (e) => {
                const p = e.target.getLatLng();
                onChange?.(+p.lat.toFixed(6), +p.lng.toFixed(6));
              },
            }}
          />
        )}
      </MapContainer>
    </div>
  );
}
